import Axios from 'axios'
import {v4 as uuidv4} from 'uuid'
import {confirmAlert} from 'react-confirm-alert'
import React, {useRef, useState} from 'react'
import styled, {keyframes} from 'styled-components'
import {organizationAccessTokenkey} from 'organization/organizationApiClient'
import {useOrganizationEvent} from 'organization/Event/OrganizationEventProvider'
import {useI18n} from 'i18n/I18nContext'
import {useTemplate} from 'TemplateEditor/TemplateProvider'
import {Template} from 'TemplateEditor/template'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import EditIcon from '@mui/icons-material/Edit'
import RefreshIcon from '@mui/icons-material/Refresh'
import CloseIcon from '@mui/icons-material/Close'
import TuneIcon from '@mui/icons-material/Tune'

/* ─── Types ───────────────────────────────────────────────────────── */

type Step = 'prompt' | 'generating' | 'preview'

interface GenerationResult {
  template: Template
  imagePaths: string[]
}

interface SectionSummary {
  type: string
  blocks: string[]
}

/* ─── Template processing (runs entirely in the browser) ──────────── */

function getNumColumns(type: string): number {
  if (type === 'section-1') return 1
  if (['section-1-1', 'section-1-2', 'section-2-1'].includes(type)) return 2
  if (['section-1-1-1', 'section-1-1-2', 'section-2-1-1'].includes(type))
    return 3
  if (type === 'section-1-1-1-1') return 4
  return 1
}

function applyMobileDefaults(block: any): any {
  const mobile: any = {...(block.mobile ?? {})}
  if (!mobile.padding) mobile.padding = {top: 10, bottom: 10, left: 0, right: 0}

  const type: string = block.type ?? ''
  const desktopSize: number = parseFloat(block.fontSize ?? 1.5)

  // Preserve template mobile typography for text blocks (AI must not change styles)
  if (
    type === 'block-normal-text' &&
    block.mobile &&
    Object.keys(block.mobile).length > 0
  ) {
    return {...block, mobile: {...block.mobile}}
  }

  if (type === 'block-normal-text') {
    if (!mobile.fontSize)
      mobile.fontSize = Math.max(Math.round(desktopSize * 0.75 * 10) / 10, 1.0)
    if (!mobile.align) mobile.align = block.align ?? 'center'
  } else if (
    ['block-button-join', 'block-button-one', 'block-button-sales'].includes(
      type,
    )
  ) {
    if (!mobile.width) mobile.width = 90
    if (!mobile.height) mobile.height = block.height ?? 50
    if (!mobile.fontSize)
      mobile.fontSize = Math.max(Math.round(desktopSize * 0.85 * 10) / 10, 1.0)
  } else if (type === 'block-image') {
    if (!mobile.width) mobile.width = 100
  } else if (type === 'block-spacer') {
    if (!mobile.height) mobile.height = Math.max((block.height ?? 30) - 10, 15)
  } else if (type === 'block-video') {
    if (!mobile.width) mobile.width = 360
    if (!mobile.height) mobile.height = 203
  }

  return {...block, mobile}
}

function processTemplateLocal(data: any): Template {
  const sections = (data.sections ?? []).map((section: any) => {
    const type: string = section.type ?? 'section-1'
    const numColumns = getNumColumns(type)

    const rawBlocks = section.blocks ?? {}
    const processedBlocks: Record<string, any[]> = {}
    const processedInnerStyles: Record<string, any> = {}

    for (let col = 1; col <= numColumns; col++) {
      const colKey = String(col)
      const columnBlocks =
        rawBlocks[col] ?? rawBlocks[colKey] ?? rawBlocks[col - 1] ?? []

      processedBlocks[colKey] = (
        Array.isArray(columnBlocks) ? columnBlocks : []
      )
        .filter(Boolean)
        .map((block: any) =>
          applyMobileDefaults({
            ...block,
            id: uuidv4(),
            visible: block.visible ?? true,
            visibility: block.visibility ?? 'all',
            visible_ticket_ids: Array.isArray(block.visible_ticket_ids)
              ? block.visible_ticket_ids
              : [],
          }),
        )

      const existingInner =
        section.innerStyles?.[col] ?? section.innerStyles?.[colKey] ?? null
      processedInnerStyles[colKey] = existingInner ?? {
        border: {color: null, width: 0, radius: 0},
        padding: {top: 0, bottom: 0, left: 0, right: 0},
        position: {vertical: 'center', horizontal: 'center'},
        background: null,
        backgroundImage: null,
      }
    }

    return {
      ...section,
      id: uuidv4(),
      background: section.background ?? null,
      visible: section.visible ?? true,
      visible_ticket_ids: Array.isArray(section.visible_ticket_ids)
        ? section.visible_ticket_ids
        : [],
      visibility: section.visibility ?? 'all',
      revertMobile: section.revertMobile ?? false,
      backgroundImage: section.backgroundImage ?? null,
      border: section.border || {color: '#ffffff', width: 0, radius: 0},
      padding: section.padding || {top: 60, bottom: 60, left: 20, right: 20},
      blocks: processedBlocks,
      innerStyles: processedInnerStyles,
    }
  })

  const legacyOrDefault =
    (data.defaultPadding &&
      typeof data.defaultPadding === 'object' &&
      data.defaultPadding) ||
    (data.globalPadding &&
      typeof data.globalPadding === 'object' &&
      data.globalPadding) ||
    null

  return {
    sections,
    ...(legacyOrDefault ? {defaultPadding: legacyOrDefault} : {}),
  }
}

/* ─── Helpers ─────────────────────────────────────────────────────── */

function createApiClient() {
  const token = localStorage.getItem(organizationAccessTokenkey)
  return Axios.create({
    baseURL: process.env.REACT_APP_BACKEND_URL,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: 'Bearer ' + token,
    },
  })
}

function sectionLabel(type: string, t: (k: string) => string): string {
  const key = `templateAi.section.${type}`
  const v = t(key)
  return v !== key ? v : type
}

function blockLabel(type: string, t: (k: string) => string): string {
  const key = `templateAi.block.${type}`
  const v = t(key)
  return v !== key ? v : type
}

function summariseTemplate(template: Template, t: (k: string) => string) {
  const sections = template.sections ?? []
  let blockCount = 0
  let joinCount = 0
  let imageCount = 0
  const sectionSummaries: SectionSummary[] = []

  sections.forEach((sec) => {
    const cols = Object.values(sec.blocks ?? {}) as any[][]
    const allBlocks = cols.flat().filter(Boolean)
    blockCount += allBlocks.length

    const blockTypes = allBlocks.map((b: any) => blockLabel(b?.type, t))
    allBlocks.forEach((b: any) => {
      if (b?.type === 'block-button-join') joinCount++
      if (b?.type === 'block-image') imageCount++
    })

    sectionSummaries.push({
      type: sec.type ?? 'section-1',
      blocks: blockTypes,
    })
  })

  return {
    sectionCount: sections.length,
    blockCount,
    joinCount,
    imageCount,
    sectionSummaries,
  }
}

/* ─── AI: текстове, цветова палитра, hero фон (шаблон от API) ─ */

/**
 * Put AI plain text into existing HTML without changing tags / styles / structure.
 */
function replacePlainTextInHtml(originalHtml: string, plain: string): string {
  if (!plain?.trim()) return originalHtml
  if (typeof window === 'undefined' || typeof DOMParser === 'undefined')
    return originalHtml
  try {
    const doc = new DOMParser().parseFromString(
      `<div id="__ai">${originalHtml}</div>`,
      'text/html',
    )
    const root = doc.getElementById('__ai')
    if (!root) return originalHtml
    const texts: Text[] = []
    const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT)
    let n: Node | null
    while ((n = walker.nextNode())) {
      const t = n as Text
      if (t.textContent && t.textContent.trim()) texts.push(t)
    }
    if (texts.length === 0) return originalHtml
    const parts = plain
      .split(/\n+/)
      .map((s) => s.trim())
      .filter(Boolean)
    if (texts.length === 1) {
      texts[0].textContent = plain.trim()
    } else if (parts.length === texts.length) {
      texts.forEach((t, i) => {
        t.textContent = parts[i]
      })
    } else {
      texts[0].textContent = plain.trim()
      for (let i = 1; i < texts.length; i++) texts[i].textContent = ''
    }
    return root.innerHTML
  } catch {
    return originalHtml
  }
}

type AiPaletteRole =
  | 'primary'
  | 'dark'
  | 'surface'
  | 'text_body'
  | 'text_on_dark'
  | 'accent_warm'
  | 'accent_green'

interface AiPaletteColors {
  primary: string
  dark: string
  surface: string
  text_body: string
  text_on_dark: string
  accent_warm: string
  accent_green: string
}

interface AiNarrowResult {
  texts: string[]
  hero_background_query: string
  palette: AiPaletteColors
}

/** Стойности от примерния шаблон „Основен“ (преди AI). */
const DEFAULT_AI_PALETTE: AiPaletteColors = {
  primary: '#46b3e6',
  dark: '#000000',
  surface: '#ffffff',
  text_body: '#333333',
  text_on_dark: '#ffffff',
  accent_warm: '#f85c66',
  accent_green: '#5ce65c',
}

const SECTION_BG_BY_HEX: Record<string, AiPaletteRole> = {
  '#46b3e6': 'primary',
  '#000000': 'dark',
  '#ffffff': 'surface',
}

const BLOCK_TEXT_BY_HEX: Record<string, AiPaletteRole> = {
  '#333333': 'text_body',
  '#ffffff': 'text_on_dark',
  '#f85c66': 'accent_warm',
}

const BUTTON_BG_BY_HEX: Record<string, AiPaletteRole> = {
  '#46b3e6': 'primary',
  '#5ce65c': 'accent_green',
  '#5be65c': 'accent_green',
}

function normalizeHexColor(input: unknown): string | null {
  if (typeof input !== 'string') return null
  let s = input.trim()
  if (!s.startsWith('#')) {
    if (/^[0-9a-fA-F]{6}$/.test(s)) s = `#${s}`
    else if (/^[0-9a-fA-F]{3}$/.test(s)) {
      s = `#${s
        .split('')
        .map((ch) => ch + ch)
        .join('')}`
    } else return null
  }
  let h = s.slice(1)
  if (h.length === 3) {
    h = h
      .split('')
      .map((ch) => ch + ch)
      .join('')
  }
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return null
  return `#${h.toLowerCase()}`
}

const SECTION_PALETTE_ROLES: AiPaletteRole[] = ['primary', 'dark', 'surface']
const TEXT_PALETTE_ROLES: AiPaletteRole[] = [
  'text_body',
  'text_on_dark',
  'accent_warm',
]
const BUTTON_PALETTE_ROLES: AiPaletteRole[] = ['primary', 'accent_green']

function mergeAiPalette(
  colors: unknown,
  previous: AiPaletteColors | null,
): AiPaletteColors {
  const d = previous ?? DEFAULT_AI_PALETTE
  if (colors === null || typeof colors !== 'object' || Array.isArray(colors)) {
    return {...d}
  }
  const c = colors as Record<string, unknown>
  const pick = (k: keyof AiPaletteColors): string =>
    normalizeHexColor(c[k]) ?? d[k]
  return {
    primary: pick('primary'),
    dark: pick('dark'),
    surface: pick('surface'),
    text_body: pick('text_body'),
    text_on_dark: pick('text_on_dark'),
    accent_warm: pick('accent_warm'),
    accent_green: pick('accent_green'),
  }
}

function resolveSectionBgRole(
  hex: string,
  previous: AiPaletteColors | null,
): AiPaletteRole | null {
  if (previous) {
    for (const role of SECTION_PALETTE_ROLES) {
      if (normalizeHexColor(previous[role]) === hex) return role
    }
    return null
  }
  return SECTION_BG_BY_HEX[hex] ?? null
}

function resolveTextRole(
  hex: string,
  previous: AiPaletteColors | null,
): AiPaletteRole | null {
  if (previous) {
    for (const role of TEXT_PALETTE_ROLES) {
      if (normalizeHexColor(previous[role]) === hex) return role
    }
    return null
  }
  return BLOCK_TEXT_BY_HEX[hex] ?? null
}

function resolveButtonBgRole(
  hex: string,
  previous: AiPaletteColors | null,
): AiPaletteRole | null {
  if (previous) {
    for (const role of BUTTON_PALETTE_ROLES) {
      if (normalizeHexColor(previous[role]) === hex) return role
    }
    return null
  }
  return BUTTON_BG_BY_HEX[hex] ?? null
}

/** previous = последно приложена палитра; null при първи път от примерния шаблон. */
function applyAiPalette(
  tmpl: any,
  palette: AiPaletteColors,
  previous: AiPaletteColors | null,
): void {
  const pick = (role: AiPaletteRole) => palette[role]

  for (const sec of tmpl.sections ?? []) {
    const ns = normalizeHexColor(sec.background)
    if (ns) {
      const role = resolveSectionBgRole(ns, previous)
      if (role) sec.background = pick(role)
    }

    const inner = sec.innerStyles
    if (inner && typeof inner === 'object') {
      for (const k of Object.keys(inner)) {
        const cell = inner[k]
        if (!cell || typeof cell !== 'object') continue
        const nb = normalizeHexColor(cell.background)
        if (nb) {
          const role = resolveSectionBgRole(nb, previous)
          if (role) cell.background = pick(role)
        }
      }
    }
  }

  walkBlocks(tmpl, (block) => {
    const fc = normalizeHexColor(block.fontColor)
    if (fc) {
      const role = resolveTextRole(fc, previous)
      if (role) block.fontColor = pick(role)
    }
    const tc = normalizeHexColor(block.textColor)
    if (tc) {
      const role = resolveTextRole(tc, previous)
      if (role) block.textColor = pick(role)
    }
    const bcb = normalizeHexColor(block.backgroundColor)
    if (bcb) {
      const role = resolveButtonBgRole(bcb, previous)
      if (role) block.backgroundColor = pick(role)
    }
    if (typeof block.background === 'string') {
      const bgs = normalizeHexColor(block.background)
      if (bgs) {
        const role = resolveSectionBgRole(bgs, previous)
        if (role) block.background = pick(role)
      }
    }
  })
}

function formatPaletteForPrompt(p: AiPaletteColors): string {
  return [
    `primary=${p.primary}`,
    `dark=${p.dark}`,
    `surface=${p.surface}`,
    `text_body=${p.text_body}`,
    `text_on_dark=${p.text_on_dark}`,
    `accent_warm=${p.accent_warm}`,
    `accent_green=${p.accent_green}`,
  ].join(', ')
}

/** Шаблон „Основен“ в API — 18 секции с фон dark/dark/primary/surface/… */
const OSNOVEN_SECTION_COUNT = 18
const OSNOVEN_SECTION_BG_ROLES: AiPaletteRole[] = [
  'dark',
  'dark',
  'primary',
  'surface',
  'primary',
  'surface',
  'primary',
  'surface',
  'primary',
  'surface',
  'primary',
  'surface',
  'primary',
  'surface',
  'primary',
  'surface',
  'primary',
  'surface',
]

/** Гарантира фонове на секциите според палитрата (не зависи от съвпадение на стари hex). */
function applyOsnovenStructuralSectionBackgrounds(
  tmpl: any,
  palette: AiPaletteColors,
): void {
  const sections = tmpl.sections ?? []
  if (sections.length !== OSNOVEN_SECTION_COUNT) return
  const pick = (role: AiPaletteRole) => palette[role]
  for (let i = 0; i < OSNOVEN_SECTION_COUNT; i++) {
    const role = OSNOVEN_SECTION_BG_ROLES[i]
    sections[i].background = pick(role)
  }
}

/** Втори проход: още канонични hex от примерния шаблон → палитра (ако първият проход ги е пропуснал). */
function applyCanonicalBlockColorSweep(
  tmpl: any,
  palette: AiPaletteColors,
): void {
  const ph = (role: AiPaletteRole) => palette[role]
  walkBlocks(tmpl, (block) => {
    const fc = normalizeHexColor(block.fontColor)
    if (fc && BLOCK_TEXT_BY_HEX[fc]) block.fontColor = ph(BLOCK_TEXT_BY_HEX[fc])
    const tc = normalizeHexColor(block.textColor)
    if (tc && BLOCK_TEXT_BY_HEX[tc]) block.textColor = ph(BLOCK_TEXT_BY_HEX[tc])
    const bcb = normalizeHexColor(block.backgroundColor)
    if (bcb && BUTTON_BG_BY_HEX[bcb])
      block.backgroundColor = ph(BUTTON_BG_BY_HEX[bcb])
    if (typeof block.background === 'string') {
      const bgs = normalizeHexColor(block.background)
      if (bgs && SECTION_BG_BY_HEX[bgs])
        block.background = ph(SECTION_BG_BY_HEX[bgs])
    }
  })
}

/** Normalize blocks to {col: blocks[]} (array or object format) */
function normalizeCols(rawBlocks: any): Record<string, any[]> {
  if (!rawBlocks) return {}
  if (!Array.isArray(rawBlocks)) return rawBlocks
  const cols: Record<string, any[]> = {}
  rawBlocks.forEach((colBlocks, i) => {
    if (Array.isArray(colBlocks) && colBlocks.length > 0) {
      cols[String(i)] = colBlocks.filter(Boolean)
    }
  })
  return cols
}

function walkBlocks(
  tmpl: any,
  cb: (block: any, secIdx: number, col: string, blockIdx: number) => void,
) {
  ;(tmpl.sections ?? []).forEach((sec: any, si: number) => {
    const cols = normalizeCols(sec.blocks)
    Object.keys(cols)
      .sort((a, b) => Number(a) - Number(b))
      .forEach((col) => {
        ;(cols[col] ?? []).forEach((block: any, bi: number) => {
          if (block) cb(block, si, col, bi)
        })
      })
  })
}

/**
 * Slots that match the seeded „Основен“ example template (section index = array index in sections[]).
 */
function isTargetAiTextBlock(
  si: number,
  col: string,
  bi: number,
  block: any,
): boolean {
  if (block.type !== 'block-normal-text') return false
  /* Hero + welcome (секция с video + текстове, индекс 1 в „Основен“) */
  if (si === 1 && col === '2' && (bi === 0 || bi === 2)) return true
  /* Въведение и описание (следваща секция) */
  if (si === 2 && col === '1' && (bi === 0 || bi === 1)) return true
  if (si === 4 && col === '1' && bi === 1) return true
  if (si === 5) return true
  if (si === 7 && ['1', '2', '3'].includes(col) && bi === 1) return true
  if (si === 9 && ['1', '2'].includes(col) && bi === 0) return true
  if (si === 11 && ['1', '2'].includes(col) && bi === 0) return true
  if (si === 13 && ['1', '2', '3'].includes(col) && bi === 2) return true
  return false
}

function buildTargetTextContext(
  tmpl: any,
  t: (k: string) => string,
): {
  textList: string
  textCount: number
} {
  const lines: string[] = []
  let slot = 0
  walkBlocks(tmpl, (block, si, col, bi) => {
    if (!isTargetAiTextBlock(si, col, bi, block)) return
    const label =
      slot < 23
        ? t(`templateAi.textSlot${slot}`)
        : t('templateAi.fieldFallback').replace('{n}', String(slot))
    const preview = (block.value ?? '')
      .replace(/<[^>]+>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 52)
    lines.push(
      t('templateAi.textSlotContextLine')
        .replace('{slot}', String(slot))
        .replace('{label}', label)
        .replace('{preview}', preview),
    )
    slot++
  })
  return {textList: lines.join('\n'), textCount: lines.length}
}

function applyTargetTexts(tmpl: any, texts: string[]): void {
  let idx = 0
  walkBlocks(tmpl, (block, si, col, bi) => {
    if (!isTargetAiTextBlock(si, col, bi, block)) return
    const v = texts[idx++]
    if (v?.trim()) block.value = replacePlainTextInHtml(block.value ?? '', v)
  })
}

function isLikelyExampleHeroBackground(bg: unknown): bg is string {
  if (typeof bg !== 'string' || !bg.trim()) return false
  return (
    bg.includes('headerBackgound') ||
    bg.includes('headerBackground') ||
    bg.includes('example-templates/example1/header') ||
    /header[Bb]ackgound\.(webp|jpe?g|png)/i.test(bg)
  )
}

/** Заменя фона на хедър секцията (пример: section-1-1 с headerBackgound.webp). */
function patchHeroBackgroundPath(tmpl: any, newPath: string): void {
  const sections = tmpl.sections ?? []
  for (const sec of sections) {
    if (isLikelyExampleHeroBackground(sec.backgroundImage)) {
      sec.backgroundImage = newPath
      return
    }
  }
  const splitLayout = sections.find((s: any) => s?.type === 'section-1-1')
  if (splitLayout) {
    splitLayout.backgroundImage = newPath
    return
  }
  const exampleBg = sections.find(
    (s: any) =>
      typeof s.backgroundImage === 'string' &&
      s.backgroundImage.includes('example-templates/'),
  )
  if (exampleBg) exampleBg.backgroundImage = newPath
}

async function fetchExampleTemplateFromApi(
  client: ReturnType<typeof createApiClient>,
  eventId: string | number,
  tr: (k: string) => string,
): Promise<any> {
  const res = await client.get(`api/event/${eventId}/example-templates`)
  const list = res.data ?? []
  if (!Array.isArray(list) || list.length === 0) {
    throw new Error(tr('templateAi.errNoExamples'))
  }
  const row = list.find((rowItem: any) => rowItem.slug === 'default') ?? list[0]
  if (!row?.template?.sections) {
    throw new Error(tr('templateAi.errTemplateInvalid'))
  }
  return JSON.parse(JSON.stringify(row.template))
}

const OPENAI_TEMPLATE_MODEL_DEFAULT = 'gpt-4.1-mini'
const OPENAI_MAX_OUTPUT_TOKENS = 2800

/* ─── Component ───────────────────────────────────────────────────── */

export function AiTemplateSidebar(props: {onBack: () => void}) {
  const {onBack} = props
  const {t} = useI18n()
  const {event} = useOrganizationEvent()
  const {template, updateTemplate, setTemplate} = useTemplate()

  const [step, setStep] = useState<Step>('prompt')
  const [prompt, setPrompt] = useState('')
  const [editCurrentDesign, setEditCurrentDesign] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [result, setResult] = useState<GenerationResult | null>(null)
  const [allImagePaths, setAllImagePaths] = useState<string[]>([])
  const [heroImageLoading, setHeroImageLoading] = useState(false)

  const [showEditBox, setShowEditBox] = useState(false)
  const [editInstruction, setEditInstruction] = useState('')

  const originalTemplateRef = useRef<Template | null>(null)
  /** Последна приложена палитра — нужна за „Промени“ върху вече оцветен шаблон. */
  const lastAppliedPaletteRef = useRef<AiPaletteColors | null>(null)

  /* ── Generate ── */

  const generate = async (instruction?: string) => {
    if (!prompt.trim()) return
    if (!event?.id) {
      setError(t('templateAi.errNoEvent'))
      return
    }

    setStep('generating')
    setError(null)
    setShowEditBox(false)
    setHeroImageLoading(false)

    /* Нулирай само при чисто „Нов дизайн“ от примерния шаблон — не при „Редактирай текущия“. */
    if (!instruction && !editCurrentDesign) {
      lastAppliedPaletteRef.current = null
    }

    if (!originalTemplateRef.current) {
      originalTemplateRef.current = JSON.parse(JSON.stringify(template))
    }

    try {
      const apiKey = process.env.REACT_APP_OPENAI_API_KEY?.trim()
      if (!apiKey) {
        throw new Error(t('templateAi.errOpenAI'))
      }
      const model =
        process.env.REACT_APP_OPENAI_TEMPLATE_MODEL ??
        OPENAI_TEMPLATE_MODEL_DEFAULT
      const client = createApiClient()

      let baseTpl: any
      if (instruction && result?.template) {
        baseTpl = JSON.parse(JSON.stringify(result.template))
      } else if (editCurrentDesign) {
        baseTpl = JSON.parse(
          JSON.stringify(originalTemplateRef.current ?? template),
        )
      } else {
        baseTpl = await fetchExampleTemplateFromApi(client, event.id, t)
      }

      const ctx = buildTargetTextContext(baseTpl, t)
      if (ctx.textCount === 0) {
        throw new Error(t('templateAi.errNoFields'))
      }

      const eventLine = t('templateAi.lineEvent').replace('{text}', prompt)
      const fieldsLine = t('templateAi.lineFields').replace(
        '{n}',
        String(ctx.textCount),
      )
      let userMsg = instruction
        ? `${eventLine}\n${t('templateAi.lineChange').replace('{text}', instruction)}\n\n${fieldsLine}${ctx.textList}`
        : `${eventLine}\n\n${fieldsLine}${ctx.textList}`

      if (instruction && lastAppliedPaletteRef.current) {
        userMsg += `\n\n${t('templateAi.linePalette').replace('{palette}', formatPaletteForPrompt(lastAppliedPaletteRef.current))}`
      }

      const paletteMergeBase = instruction
        ? lastAppliedPaletteRef.current
        : null
      const ai = await callOpenAI(
        t,
        apiKey,
        model,
        userMsg,
        ctx.textCount,
        paletteMergeBase,
      )
      const palettePrevious = instruction ? lastAppliedPaletteRef.current : null

      applyTargetTexts(baseTpl, ai.texts)
      applyAiPalette(baseTpl, ai.palette, palettePrevious)
      applyOsnovenStructuralSectionBackgrounds(baseTpl, ai.palette)
      applyCanonicalBlockColorSweep(baseTpl, ai.palette)
      lastAppliedPaletteRef.current = {...ai.palette}

      const tmpl = processTemplateLocal(baseTpl)

      const shouldFetchHero = !instruction && !editCurrentDesign
      const carryImagePaths =
        instruction || editCurrentDesign ? (result?.imagePaths ?? []) : []

      setStep('preview')
      setEditInstruction('')

      if (shouldFetchHero) {
        setResult({template: tmpl, imagePaths: []})
        setTemplate(tmpl)

        setHeroImageLoading(true)
        try {
          const q = (
            ai.hero_background_query ?? 'event stage lights audience'
          ).trim()
          const hb = await client.post(
            `api/event/${event.id}/ai-template/hero-background`,
            {
              image_query: q,
            },
          )
          const path = hb.data?.path as string | undefined
          if (path) {
            setAllImagePaths((prev) => [...prev, path])
            const patched = JSON.parse(JSON.stringify(tmpl)) as Template
            patchHeroBackgroundPath(patched, path)
            setTemplate(patched)
            setResult({template: patched, imagePaths: [path]})
          }
        } catch {
          /* hero optional */
        } finally {
          setHeroImageLoading(false)
        }
      } else {
        setResult({template: tmpl, imagePaths: carryImagePaths})
        setTemplate(tmpl)
      }
    } catch (err: any) {
      setError(err?.message ?? t('templateAi.errUnknown'))
      setStep('prompt')
    }
  }

  const callOpenAI = async (
    tr: (k: string) => string,
    apiKey: string,
    model: string,
    userMessage: string,
    textCount: number,
    paletteMergeBase: AiPaletteColors | null,
  ): Promise<AiNarrowResult> => {
    const res = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${apiKey}`,
      },
      body: JSON.stringify({
        model,
        messages: [
          {role: 'system', content: tr('templateAi.openaiSystem')},
          {role: 'user', content: userMessage},
        ],
        response_format: {type: 'json_object'},
        temperature: 0.7,
        max_tokens: OPENAI_MAX_OUTPUT_TOKENS,
      }),
    })

    if (!res.ok) {
      const e = await res.json().catch(() => ({}))
      throw new Error(
        e?.error?.message ??
          tr('templateAi.errOpenAIHttp').replace(
            '{status}',
            String(res.status),
          ),
      )
    }

    const data = await res.json()
    const finishReason: string = data.choices?.[0]?.finish_reason ?? ''
    let raw: string = (data.choices?.[0]?.message?.content ?? '').trim()
    raw = raw
      .replace(/^```(?:json)?\s*/i, '')
      .replace(/\s*```\s*$/i, '')
      .trim()

    let parsed: any
    try {
      parsed = JSON.parse(raw)
    } catch {
      const hint =
        finishReason === 'length' ? tr('templateAi.errTruncatedSuffix') : ''
      throw new Error(`${tr('templateAi.errInvalidJson')}${hint}`)
    }

    if (!parsed?.texts) {
      throw new Error(tr('templateAi.errIncompleteResponse'))
    }

    function fitStrings(arr: string[] | undefined, len: number): string[] {
      const a = [...(arr ?? [])].map(String)
      while (a.length < len) a.push('')
      return a.slice(0, len)
    }

    return {
      texts: fitStrings(parsed.texts, textCount),
      hero_background_query: String(
        parsed.hero_background_query ?? 'conference hall lights wide',
      ),
      palette: mergeAiPalette(parsed.colors, paletteMergeBase),
    }
  }

  /* ── Approve ── */

  const approve = () => {
    if (!result) return
    updateTemplate(template)
    originalTemplateRef.current = null
    lastAppliedPaletteRef.current = null
    setAllImagePaths([])
    onBack()
  }

  /* ── Reject ── */

  const reject = () => {
    confirmAlert({
      message: t('templateAi.rejectMsg'),
      closeOnClickOutside: false,
      buttons: [
        {
          label: t('templateAi.rejectYes'),
          onClick: async () => {
            const original = originalTemplateRef.current
            if (original) setTemplate(original)
            cleanupImages(allImagePaths)
            originalTemplateRef.current = null
            lastAppliedPaletteRef.current = null
            setResult(null)
            setAllImagePaths([])
            setEditInstruction('')
            setShowEditBox(false)
            setStep('prompt')
          },
        },
        {label: t('templateAi.rejectNo'), onClick: () => {}},
      ],
    })
  }

  /* ── Cleanup images from S3 ── */

  const cleanupImages = async (paths: string[]) => {
    if (!event || paths.length === 0) return
    try {
      await createApiClient().post(
        `api/event/${event.id}/ai-template/cleanup`,
        {paths},
      )
    } catch {
      // Non-critical
    }
  }

  /* ── Edit ── */

  const applyEdit = () => {
    if (!editInstruction.trim()) return
    generate(editInstruction)
  }

  const backToPrompt = () => {
    if (result) {
      setShowEditBox(false)
      setStep('prompt')
    } else {
      onBack()
    }
  }

  const summary = result ? summariseTemplate(result.template, t) : null

  /* ─────────────────────────────────────────────────────── RENDER ── */

  return (
    <Container>
      {/* ── HEADER ── */}
      <Header>
        <BackBtn
          onClick={step === 'preview' ? backToPrompt : onBack}
          title={t('templateAi.backAria')}
        >
          <ArrowBackIcon fontSize="small" />
        </BackBtn>
        <HeaderTitle>
          <AutoAwesomeIcon fontSize="small" style={{color: '#a78bfa'}} />
          {t('templateAi.headerTitle')}
        </HeaderTitle>
        {step === 'preview' && (
          <PreviewBadge>{t('templateAi.previewBadge')}</PreviewBadge>
        )}
      </Header>

      {/* ════════════════════ STEP: PROMPT ════════════════════ */}
      {step === 'prompt' && (
        <ScrollContent>
          {error && <ErrorBox>{error}</ErrorBox>}

          {result && (
            <ReturnToPreviewBtn onClick={() => setStep('preview')}>
              {t('templateAi.returnPreview')}
            </ReturnToPreviewBtn>
          )}

          <ModeRow>
            <ModeBtn
              active={!editCurrentDesign}
              onClick={() => setEditCurrentDesign(false)}
            >
              {t('templateAi.newDesign')}
            </ModeBtn>
            <ModeBtn
              active={editCurrentDesign}
              onClick={() => setEditCurrentDesign(true)}
            >
              <TuneIcon style={{fontSize: 14}} /> {t('templateAi.editCurrent')}
            </ModeBtn>
          </ModeRow>

          <FieldLabel>
            {editCurrentDesign
              ? t('templateAi.labelEdit')
              : t('templateAi.labelNew')}
          </FieldLabel>
          <Textarea
            rows={editCurrentDesign ? 3 : 4}
            placeholder={
              editCurrentDesign ? t('templateAi.phEdit') : t('templateAi.phNew')
            }
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
          />

          <GenerateBtn onClick={() => generate()} disabled={!prompt.trim()}>
            <AutoAwesomeIcon fontSize="small" />
            {editCurrentDesign
              ? t('templateAi.btnEdit')
              : t('templateAi.btnNew')}
          </GenerateBtn>

          <HintText>{t('templateAi.hint')}</HintText>
        </ScrollContent>
      )}

      {/* ════════════════════ STEP: GENERATING ════════════════ */}
      {step === 'generating' && (
        <CenteredContent>
          <PulseRing />
          <GeneratingTitle>{t('templateAi.generatingTitle')}</GeneratingTitle>
          <GeneratingSub>
            {t('templateAi.generatingSub')}
            <br />
            {t('templateAi.generatingTime')}
          </GeneratingSub>
          <GeneratingSteps>
            <GeneratingStepLine>{t('templateAi.genStep1')}</GeneratingStepLine>
            <GeneratingStepLine>{t('templateAi.genStep2')}</GeneratingStepLine>
            <GeneratingStepLine>{t('templateAi.genStep3')}</GeneratingStepLine>
          </GeneratingSteps>
        </CenteredContent>
      )}

      {/* ════════════════════ STEP: PREVIEW ══════════════════ */}
      {step === 'preview' && summary && (
        <PreviewWrap>
          <ScrollContent>
            <StatsCard>
              <StatRow>
                <StatIcon>📐</StatIcon>
                <StatVal>
                  {t('templateAi.statsSections').replace(
                    '{n}',
                    String(summary.sectionCount),
                  )}
                </StatVal>
                <StatDiv />
                <StatIcon>🧩</StatIcon>
                <StatVal>
                  {t('templateAi.statsBlocks').replace(
                    '{n}',
                    String(summary.blockCount),
                  )}
                </StatVal>
              </StatRow>
              <StatRow>
                <StatIcon
                  style={{color: summary.joinCount > 0 ? '#4ade80' : '#f87171'}}
                >
                  {summary.joinCount > 0 ? '✅' : '⚠️'}
                </StatIcon>
                <StatVal>
                  {summary.joinCount > 0
                    ? t('templateAi.joinOk').replace(
                        '{n}',
                        String(summary.joinCount),
                      )
                    : t('templateAi.joinMissing')}
                </StatVal>
                {summary.imageCount > 0 && (
                  <>
                    <StatDiv />
                    <StatIcon>🖼️</StatIcon>
                    <StatVal>
                      {t('templateAi.statsImages').replace(
                        '{n}',
                        String(summary.imageCount),
                      )}
                    </StatVal>
                  </>
                )}
              </StatRow>
            </StatsCard>

            <SectionList>
              {summary.sectionSummaries.map((sec, i) => (
                <SectionItem key={i}>
                  <SIdx>{i + 1}</SIdx>
                  <SInfo>
                    <SType>{sectionLabel(sec.type, t)}</SType>
                    <SBlocks>
                      {sec.blocks.slice(0, 6).join(' · ')}
                      {sec.blocks.length > 6 && ' …'}
                    </SBlocks>
                  </SInfo>
                </SectionItem>
              ))}
            </SectionList>

            <PreviewNote>{t('templateAi.scrollNote')}</PreviewNote>

            <ApproveBtn onClick={approve} disabled={heroImageLoading}>
              <CheckCircleIcon fontSize="small" />
              {t('templateAi.approve')}
            </ApproveBtn>

            {showEditBox ? (
              <EditBox>
                <EditBoxHeader>
                  <span>{t('templateAi.whatChange')}</span>
                  <CloseEditBtn
                    onClick={() => {
                      setShowEditBox(false)
                      setEditInstruction('')
                    }}
                  >
                    <CloseIcon style={{fontSize: 14}} />
                  </CloseEditBtn>
                </EditBoxHeader>
                <EditTextarea
                  rows={3}
                  placeholder={t('templateAi.editInstructionPh')}
                  value={editInstruction}
                  onChange={(e) => setEditInstruction(e.target.value)}
                  autoFocus
                />
                <EditApplyBtn
                  onClick={applyEdit}
                  disabled={!editInstruction.trim()}
                >
                  <RefreshIcon fontSize="small" />
                  {t('templateAi.regen')}
                </EditApplyBtn>
              </EditBox>
            ) : (
              <SecondaryRow>
                <SecBtn onClick={() => setShowEditBox(true)}>
                  <EditIcon style={{fontSize: 15}} /> {t('templateAi.change')}
                </SecBtn>
                <RejectBtn onClick={reject}>
                  <CloseIcon style={{fontSize: 15}} /> {t('templateAi.cancel')}
                </RejectBtn>
              </SecondaryRow>
            )}
          </ScrollContent>
          {heroImageLoading && (
            <HeroImageLoadingOverlay role="status" aria-live="polite">
              <HeroImageLoadingInner>
                <PulseRingSmall />
                <HeroImageLoadingText>
                  {t('templateAi.heroLoading')}
                </HeroImageLoadingText>
              </HeroImageLoadingInner>
            </HeroImageLoadingOverlay>
          )}
        </PreviewWrap>
      )}
    </Container>
  )
}

/* ─── Animations ──────────────────────────────────────────────────── */

const pulse = keyframes`
  0%   { transform: scale(1);    opacity: 1; }
  50%  { transform: scale(1.12); opacity: 0.6; }
  100% { transform: scale(1);    opacity: 1; }
`

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
`

const blink = keyframes`
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.4; }
`

/* ─── Styled components ───────────────────────────────────────────── */

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  animation: ${fadeIn} 0.2s ease;
`

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px 12px;
  border-bottom: 1px solid rgba(167, 139, 250, 0.2);
  flex-shrink: 0;
`

const BackBtn = styled.button`
  background: rgba(255, 255, 255, 0.08);
  border: none;
  color: #fff;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.15s;
  &:hover {
    background: rgba(255, 255, 255, 0.18);
  }
`

const HeaderTitle = styled.div`
  color: #fff;
  font-weight: 700;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
`

const PreviewBadge = styled.span`
  background: linear-gradient(135deg, #7c3aed, #4f46e5);
  color: #fff;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.08em;
  padding: 3px 7px;
  border-radius: 20px;
`

const ScrollContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 12px 14px 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.15) transparent;
`

const PreviewWrap = styled.div`
  flex: 1;
  position: relative;
  min-height: 0;
  display: flex;
  flex-direction: column;
`

const HeroImageLoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(15, 15, 20, 0.72);
  backdrop-filter: blur(4px);
`

const HeroImageLoadingInner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  padding: 20px;
`

const PulseRingSmall = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #7c3aed, #4f46e5);
  animation: ${pulse} 1.4s ease-in-out infinite;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 0 8px rgba(124, 58, 237, 0.12);
  &::after {
    content: '✦';
    color: #fff;
    font-size: 16px;
  }
`

const HeroImageLoadingText = styled.div`
  color: rgba(255, 255, 255, 0.85);
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.02em;
`

const CenteredContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 30px 20px;
  gap: 12px;
`

const ErrorBox = styled.div`
  background: rgba(239, 68, 68, 0.15);
  border: 1px solid rgba(239, 68, 68, 0.35);
  color: #fca5a5;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 12px;
  line-height: 1.5;
`

const ReturnToPreviewBtn = styled.button`
  background: rgba(139, 92, 246, 0.15);
  border: 1px solid rgba(139, 92, 246, 0.3);
  color: #c4b5fd;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 12px;
  cursor: pointer;
  text-align: left;
  transition: background 0.15s;
  &:hover {
    background: rgba(139, 92, 246, 0.25);
  }
`

const ModeRow = styled.div`
  display: flex;
  gap: 6px;
`

const ModeBtn = styled.button<{active?: boolean}>`
  flex: 1;
  background: ${(p) =>
    p.active ? 'rgba(124,58,237,0.35)' : 'rgba(255,255,255,0.06)'};
  border: 1px solid
    ${(p) => (p.active ? 'rgba(124,58,237,0.6)' : 'rgba(255,255,255,0.1)')};
  color: ${(p) => (p.active ? '#e9d5ff' : 'rgba(255,255,255,0.5)')};
  border-radius: 8px;
  padding: 7px 8px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  transition: all 0.15s;
  &:hover {
    background: rgba(124, 58, 237, 0.2);
    color: #e9d5ff;
  }
`

const FieldLabel = styled.div`
  color: rgba(255, 255, 255, 0.5);
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`

const Textarea = styled.textarea`
  width: 100%;
  background: rgba(255, 255, 255, 0.07);
  border: 1px solid rgba(255, 255, 255, 0.13);
  border-radius: 8px;
  color: #fff;
  font-size: 13px;
  padding: 10px 12px;
  resize: vertical;
  line-height: 1.5;
  font-family: inherit;
  box-sizing: border-box;
  transition: border-color 0.15s;
  &::placeholder {
    color: rgba(255, 255, 255, 0.25);
  }
  &:focus {
    outline: none;
    border-color: rgba(139, 92, 246, 0.6);
  }
`

const GenerateBtn = styled.button`
  background: linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%);
  color: #fff;
  border: none;
  border-radius: 10px;
  padding: 13px 16px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition:
    opacity 0.15s,
    transform 0.1s;
  box-shadow: 0 4px 15px rgba(124, 58, 237, 0.35);
  &:hover:not(:disabled) {
    opacity: 0.9;
    transform: translateY(-1px);
  }
  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
    box-shadow: none;
  }
`

const HintText = styled.div`
  color: rgba(255, 255, 255, 0.3);
  font-size: 11px;
  line-height: 1.6;
  text-align: center;
`

const PulseRing = styled.div`
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: linear-gradient(135deg, #7c3aed, #4f46e5);
  animation: ${pulse} 1.4s ease-in-out infinite;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow:
    0 0 0 12px rgba(124, 58, 237, 0.12),
    0 0 0 24px rgba(124, 58, 237, 0.06);
  &::after {
    content: '✦';
    color: #fff;
    font-size: 22px;
  }
`

const GeneratingTitle = styled.div`
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  margin-top: 8px;
`

const GeneratingSub = styled.div`
  color: rgba(255, 255, 255, 0.4);
  font-size: 12px;
  text-align: center;
  line-height: 1.7;
`

const GeneratingSteps = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 8px;
  width: 100%;
`

const GeneratingStepLine = styled.div`
  color: rgba(167, 139, 250, 0.7);
  font-size: 11px;
  animation: ${blink} 2s ease-in-out infinite;
  text-align: center;
  &:nth-child(2) {
    animation-delay: 0.5s;
  }
  &:nth-child(3) {
    animation-delay: 1s;
  }
`

const StatsCard = styled.div`
  background: rgba(124, 58, 237, 0.12);
  border: 1px solid rgba(124, 58, 237, 0.25);
  border-radius: 10px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const StatRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
`

const StatIcon = styled.span`
  font-size: 13px;
`

const StatVal = styled.span`
  color: rgba(255, 255, 255, 0.8);
  font-size: 12px;
  font-weight: 500;
`

const StatDiv = styled.span`
  width: 1px;
  height: 12px;
  background: rgba(255, 255, 255, 0.15);
  margin: 0 2px;
`

const SectionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  max-height: 200px;
  overflow-y: auto;
  scrollbar-width: thin;
`

const SectionItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 7px;
  padding: 5px 7px;
  background: rgba(255, 255, 255, 0.04);
  border-radius: 6px;
  &:hover {
    background: rgba(255, 255, 255, 0.07);
  }
`

const SIdx = styled.div`
  width: 18px;
  height: 18px;
  background: rgba(124, 58, 237, 0.4);
  border-radius: 4px;
  color: #c4b5fd;
  font-size: 10px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`

const SInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
`

const SType = styled.div`
  color: rgba(255, 255, 255, 0.65);
  font-size: 11px;
  font-weight: 600;
`

const SBlocks = styled.div`
  color: rgba(255, 255, 255, 0.35);
  font-size: 10px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const PreviewNote = styled.div`
  text-align: center;
  color: rgba(255, 255, 255, 0.3);
  font-size: 11px;
  font-style: italic;
  padding: 2px 0;
`

const ApproveBtn = styled.button`
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
  color: #fff;
  border: none;
  border-radius: 10px;
  padding: 13px 16px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-shadow: 0 4px 15px rgba(5, 150, 105, 0.3);
  transition:
    opacity 0.15s,
    transform 0.1s;
  &:hover:not(:disabled) {
    opacity: 0.9;
    transform: translateY(-1px);
  }
  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`

const EditBox = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  animation: ${fadeIn} 0.2s ease;
`

const EditBoxHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: rgba(255, 255, 255, 0.6);
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`

const CloseEditBtn = styled.button`
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.4);
  cursor: pointer;
  padding: 2px;
  display: flex;
  align-items: center;
  &:hover {
    color: #fff;
  }
`

const EditTextarea = styled(Textarea)`
  font-size: 12px;
`

const EditApplyBtn = styled.button`
  background: rgba(124, 58, 237, 0.4);
  border: 1px solid rgba(124, 58, 237, 0.5);
  color: #e9d5ff;
  border-radius: 8px;
  padding: 9px 12px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: background 0.15s;
  &:hover:not(:disabled) {
    background: rgba(124, 58, 237, 0.6);
  }
  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
`

const SecondaryRow = styled.div`
  display: flex;
  gap: 8px;
`

const SecBtn = styled.button`
  flex: 1;
  background: rgba(255, 255, 255, 0.07);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.65);
  border-radius: 8px;
  padding: 9px 10px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  transition:
    background 0.15s,
    color 0.15s;
  &:hover {
    background: rgba(255, 255, 255, 0.12);
    color: #fff;
  }
`

const RejectBtn = styled(SecBtn)`
  border-color: rgba(239, 68, 68, 0.25);
  color: rgba(252, 165, 165, 0.7);
  &:hover {
    background: rgba(239, 68, 68, 0.12);
    color: #fca5a5;
    border-color: rgba(239, 68, 68, 0.4);
  }
`
